import { TextField, Stack, Typography } from "@mui/material";
import { useRef, useEffect, useState } from "react";

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  length?: number;
}

export const OTPInput = ({ value, onChange, error, helperText, length = 6 }: OTPInputProps) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Синхронизируем внутреннее состояние с внешним значением
    if (value.length <= length) {
      const newOtp = Array(length).fill('').map((_, i) => value[i] || '');
      setOtp(newOtp);
    }
  }, [value, length]);

  const handleChange = (index: number, newValue: string) => {
    // Разрешаем только цифры
    const digit = newValue.replace(/\D/g, '');
    
    if (digit.length > 1) {
      // Если вставлено несколько символов, берем только первый
      const firstDigit = digit[0];
      const newOtp = [...otp];
      newOtp[index] = firstDigit;
      setOtp(newOtp);
      
      // Переходим к следующему полю
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      
      onChange(newOtp.join(''));
    } else {
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);
      onChange(newOtp.join(''));
      
      // Автоматический переход к следующему полю
      if (digit && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Если поле пустое и нажат Backspace, переходим к предыдущему полю
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    
    if (pastedData.length > 0) {
      const newOtp = Array(length).fill('').map((_, i) => pastedData[i] || '');
      setOtp(newOtp);
      onChange(newOtp.join(''));
      
      // Фокус на последнее заполненное поле или следующее пустое
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ width: '100%' }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            error={error}
            inputProps={{
              maxLength: 1,
              style: { textAlign: 'center', fontSize: '1.5rem', padding: '8px' }
            }}
            sx={{
              width: '56px',
              '& .MuiOutlinedInput-root': {
                '& input': {
                  textAlign: 'center',
                }
              }
            }}
            autoFocus={index === 0 && !otp[0]}
          />
        ))}
      </Stack>
      {helperText && (
        <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ textAlign: 'center', width: '100%' }}>
          {helperText}
        </Typography>
      )}
    </Stack>
  );
};

